import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

interface Props {
  children: ReactNode;
  /** Optional fallback to render instead of the default error UI */
  fallback?: (reset: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  private reset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback(this.reset);
      }

      return (
        <div className="min-h-[400px] flex items-center justify-center p-8">
          <div className="max-w-lg w-full">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-4">
              <h2 className="text-lg font-semibold text-red-800 mb-2">Something went wrong</h2>
              <p className="text-sm text-red-700 font-mono break-words">
                {this.state.error?.message ?? 'An unexpected error occurred'}
              </p>
            </div>

            {(this.state.error?.stack || this.state.errorInfo?.componentStack) && (
              <details className="mb-4">
                <summary className="text-sm font-medium text-gray-600 cursor-pointer hover:text-gray-800 mb-2">
                  Show details
                </summary>
                <pre className="text-xs text-gray-600 bg-gray-100 rounded-lg p-4 overflow-auto max-h-48 whitespace-pre-wrap break-words">
                  {this.state.error?.stack}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="flex gap-3">
              <button
                onClick={this.reset}
                className="px-4 py-2 bg-primary-400 text-neutral-900 font-semibold rounded-lg hover:bg-primary-500 transition-colors text-sm"
              >
                Try again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
